import { ApplicationConfig, ErrorHandler, importProvidersFrom } from "@angular/core";
import { TitleStrategy, provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from "@angular/router";

import { routes } from "./app.routes";
import { DecimalPipe, CurrencyPipe, DatePipe, PercentPipe } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { HttpErrorInterceptor } from "./shared/interceptors/httpErrorInterceptor";
import { GlobalErrorHandlerService } from "./shared/services/global-error-handler.service";
import { provideAnimations } from "@angular/platform-browser/animations";
import { ApiModule } from "./shared/generated/api.module";
import { Configuration } from "./shared/generated/configuration";
import { PageTitleStrategy } from "./strategies/page-title-strategy";
import { PhonePipe } from "./shared/pipes/phone.pipe";
import { GroupByPipe } from "./shared/pipes/group-by.pipe";
import { provideDialogConfig } from "@ngneat/dialog";
import { SumPipe } from "./shared/pipes/sum.pipe";
import { provideAuth0 } from "@auth0/auth0-angular";
import { buildAuth0AllowedList } from "./shared/generated/auth0-allowedlist";
import { e2eAuthInterceptor } from "./shared/interceptors/e2e-auth.interceptor";
import { anonymousAwareAuthInterceptor } from "./shared/interceptors/anonymous-aware-auth.interceptor";

// When running under Playwright e2e tests, localStorage.__e2e_globalID is set
// before Angular bootstraps. In that case, give Auth0 an empty allowedList so
// its interceptor becomes a no-op (won't block requests for missing tokens).
const isE2E = !!localStorage.getItem("__e2e_globalID");

const isUnderDomain = (hostname: string, domain: string) => hostname === domain || hostname.endsWith("." + domain);

export const appConfig: ApplicationConfig = {
    providers: [
        // Single router registration carrying all router features. withComponentInputBinding()
        // must share the same provideRouter as the inheritance strategy: under the default
        // 'emptyOnly' the nested wizard step routes don't inherit the parent's
        // mooringRegistrationSubmissionID, so each step's @Input() binds null in Edit mode
        // (breaks Save/Delete). 'always' makes child steps receive the parent route param.
        provideRouter(
            routes,
            withComponentInputBinding(),
            withRouterConfig({ paramsInheritanceStrategy: "always" }),
            withInMemoryScrolling({ scrollPositionRestoration: "enabled", anchorScrolling: "enabled" }),
        ),
        provideAuth0({
            domain: environment.auth0.domain,
            clientId: environment.auth0.clientId,
            authorizationParams: {
                redirect_uri: window.location.origin,
                audience: environment.auth0.audience,
                scope: "openid profile email offline_access",
            },
            useRefreshTokens: true,
            // When a tab has no refresh token in its (per-origin) localStorage — e.g. a cross-subdomain
            // tab opened from a [areaLink] like "Preview in Threshold Dashboard", which lands on
            // threshold.* while the user authenticated on ltinfo.* — fall back to the iframe silent-auth
            // flow against the shared Auth0 SSO session instead of throwing missing_refresh_token. Keeps
            // cross-area deep links (URL traceability) working for authenticated/secured calls.
            useRefreshTokensFallback: true,
            cacheLocation: "localstorage",
            // Share the is.authenticated cookie across area subdomains so the startup checkSession
            // restores the session when landing on a different area (localStorage is per-origin).
            // anonymousAwareAuthInterceptor relies on this to tell a signed-in user from an anonymous
            // visitor. A browser drops a cookie whose domain the page isn't under (e.g. plain
            // localhost), so only set it when it applies.
            cookieDomain: isUnderDomain(window.location.hostname, environment.auth0.cookieDomain)
                ? environment.auth0.cookieDomain
                : undefined,
            httpInterceptor: {
                allowedList: isE2E ? [] : buildAuth0AllowedList(environment.mainAppApiUrl),
            },
        }),
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([e2eAuthInterceptor, anonymousAwareAuthInterceptor])),
        importProvidersFrom(
            ApiModule.forRoot(() => {
                return new Configuration({
                    basePath: `${environment.mainAppApiUrl}`,
                });
            })
        ),
        provideAnimations(),
        { provide: TitleStrategy, useClass: PageTitleStrategy },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandlerService,
        },
        DecimalPipe,
        CurrencyPipe,
        PercentPipe,
        DatePipe,
        PhonePipe,
        GroupByPipe,
        SumPipe,
        provideDialogConfig({
            sizes: {
                sm: {
                    width: "100%",
                    maxWidth: "540px",
                    maxHeight: "90vh",
                },
                lg: {
                    width: "100%",
                    maxWidth: "1280px",
                    maxHeight: "90vh",
                },
            },
        }),
    ],
};
